import { useAppSelector } from '../../../../redux/store';
import {
  User,
  EnvelopeSimple,
  Phone,
  Briefcase,
  Gear,
  Buildings,
  PencilSimple,
  MapPin,
  UserCircle,
  Storefront,
  Clock,
  CreditCard,
  Globe,
  FileText,
} from 'phosphor-react';
import { COLORS } from '../../../../utils/colors';
import { AppButton } from '../../../general/AppButton/AppButton';
import { useMemo, useState } from 'react';
import { capitalizeWordsInSentence } from '@dill/dill-shared/dist/src';
import EditProfileModal from '../EditProfileModal';

// Helper Components - defined before main component
const InfoItem = ({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value?: string | null;
}) => (
  <div
    className="flex items-start gap-3 p-3 rounded-lg"
    style={{ backgroundColor: COLORS.GREY_50 }}>
    <div style={{ color: COLORS.PRIMARY_500 }} className="mt-0.5">
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-xs font-medium mb-0.5" style={{ color: COLORS.TEXT_SECONDARY }}>
        {label}
      </p>
      <p className="text-sm font-medium truncate" style={{ color: COLORS.TEXT_PRIMARY }}>
        {value || 'Not provided'}
      </p>
    </div>
  </div>
);

const PillsList = ({
  items,
  label,
  icon,
  emptyMessage = 'None added',
}: {
  items?: string[] | null;
  label: string;
  icon: React.ReactNode;
  emptyMessage?: string;
}) => (
  <div
    className="flex items-start gap-3 p-3 rounded-lg"
    style={{ backgroundColor: COLORS.GREY_50 }}>
    <div style={{ color: COLORS.PRIMARY_500 }} className="mt-0.5">
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-xs font-medium mb-1.5" style={{ color: COLORS.TEXT_SECONDARY }}>
        {label}
      </p>
      <div className="flex flex-wrap gap-1.5">
        {items && items.length > 0 ? (
          items.map((item, index) => (
            <span
              key={index}
              className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium"
              style={{
                backgroundColor: COLORS.PRIMARY_50,
                color: COLORS.PRIMARY_700,
              }}>
              {item}
            </span>
          ))
        ) : (
          <span className="text-sm" style={{ color: COLORS.TEXT_TERTIARY }}>
            {emptyMessage}
          </span>
        )}
      </div>
    </div>
  </div>
);

const BranchCard = ({
  branchName,
  branchAddress,
  branchContact,
  salesRep,
}: {
  branchName?: string;
  branchAddress?: string;
  branchContact?: string;
  salesRep?: Array<{ name?: string; email?: string; phone?: string }>;
}) => (
  <div
    className="p-4 rounded-lg border"
    style={{ backgroundColor: COLORS.GREY_50, borderColor: COLORS.GREY_200 }}>
    <div className="flex items-start gap-3">
      <div style={{ color: COLORS.PRIMARY_500 }}>
        <Storefront size={20} weight="duotone" />
      </div>
      <div className="flex-1">
        <p className="text-sm font-semibold mb-2" style={{ color: COLORS.TEXT_PRIMARY }}>
          {branchName || 'Unnamed Branch'}
        </p>
        {branchAddress && (
          <p className="text-xs mb-1" style={{ color: COLORS.TEXT_SECONDARY }}>
            {branchAddress}
          </p>
        )}
        {branchContact && (
          <p className="text-xs mb-2" style={{ color: COLORS.TEXT_SECONDARY }}>
            Contact: {branchContact}
          </p>
        )}
        {salesRep && salesRep.length > 0 && (
          <div className="mt-2">
            <p className="text-xs font-medium mb-1" style={{ color: COLORS.TEXT_TERTIARY }}>
              Sales Representatives
            </p>
            <div className="space-y-1">
              {salesRep.map((rep, index) => (
                <div key={index} className="text-xs" style={{ color: COLORS.TEXT_SECONDARY }}>
                  {rep.name && <span className="font-medium">{rep.name}</span>}
                  {rep.email && <span className="ml-1">({rep.email})</span>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  </div>
);

// Main Component
const SupplierUserProfile = () => {
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const [activeTab, setActiveTab] = useState<'overview' | 'settings'>('overview');
  const [isEditProfileModalOpen, setIsEditProfileModalOpen] = useState(false);

  const userInitials = `${user?.firstName?.charAt(0) || ''}${user?.lastName?.charAt(0) || ''}`;
  const fullName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim();

  const supplier = useMemo(() => {
    return currentSupplier;
  }, [currentSupplier?.id]);

  const tabs = [{ id: 'overview' as const, label: 'Overview', icon: User }];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Profile Header with Gradient Background */}
      <div
        className="relative h-48"
        style={{
          background: `linear-gradient(135deg, ${COLORS.PRIMARY_600} 0%, ${COLORS.PRIMARY_800} 100%)`,
        }}>
        {/* Decorative circles */}
        <div
          className="absolute top-4 right-20 w-32 h-32 rounded-full opacity-10"
          style={{ backgroundColor: COLORS.WHITE }}
        />
        <div
          className="absolute bottom-8 right-40 w-20 h-20 rounded-full opacity-10"
          style={{ backgroundColor: COLORS.WHITE }}
        />
        <div
          className="absolute top-12 right-60 w-12 h-12 rounded-full opacity-10"
          style={{ backgroundColor: COLORS.WHITE }}
        />
      </div>

      {/* Profile Content Container */}
      <div className="max-w-6xl mx-auto px-6 -mt-24">
        {/* Profile Card */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Profile Header Section */}
          <div className="px-8 pt-6 pb-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-end gap-6">
              {/* Avatar */}
              <div
                className="relative flex items-center justify-center w-32 h-32 rounded-2xl text-white text-4xl font-semibold shadow-xl ring-4 ring-white"
                style={{ backgroundColor: COLORS.PRIMARY_500 }}>
                {userInitials || <User size={48} weight="thin" />}
                {/* Status indicator */}
                <div
                  className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full border-4 border-white"
                  style={{ backgroundColor: COLORS.SUCCESS_500 }}
                />
              </div>

              {/* User Info */}
              <div className="flex-1 z-20">
                <div className="flex items-center gap-3 mb-1">
                  <h1 className="text-2xl font-bold text-white">
                    {fullName ? capitalizeWordsInSentence(fullName) : 'User Profile'}
                  </h1>
                </div>
                <p className="text-sm mb-3" style={{ color: COLORS.TEXT_SECONDARY }}>
                  {user?.title || 'Team Member'}
                </p>
                <div
                  className="flex flex-wrap gap-4 text-sm"
                  style={{ color: COLORS.TEXT_SECONDARY }}>
                  <div className="flex items-center gap-2">
                    <EnvelopeSimple
                      size={16}
                      weight="duotone"
                      style={{ color: COLORS.PRIMARY_500 }}
                    />
                    <span>{user?.email || 'No email'}</span>
                  </div>
                </div>
              </div>

              {/* Edit Profile Button */}
              <AppButton
                text="Edit Profile"
                icon={<PencilSimple size={18} weight="bold" />}
                type="SECONDARY"
                onClick={() => setIsEditProfileModalOpen(true)}
              />
            </div>
          </div>

          {/* Tabs Navigation */}
          <div className="flex border-b px-8" style={{ borderColor: COLORS.GREY_200 }}>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-4 text-sm font-medium transition-all border-b-2 -mb-px ${
                  activeTab === tab.id ? '' : 'border-transparent hover:border-gray-300'
                }`}
                style={{
                  color: activeTab === tab.id ? COLORS.PRIMARY_600 : COLORS.TEXT_SECONDARY,
                  borderColor: activeTab === tab.id ? COLORS.PRIMARY_600 : 'transparent',
                }}>
                <tab.icon size={18} weight={activeTab === tab.id ? 'fill' : 'regular'} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6 pb-8">
          {/* Left Column - Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Contact Information Card */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2
                className="text-lg font-semibold mb-4 flex items-center gap-2"
                style={{ color: COLORS.TEXT_PRIMARY }}>
                <User size={20} weight="duotone" style={{ color: COLORS.PRIMARY_500 }} />
                User Information
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <InfoItem
                  icon={<UserCircle size={18} weight="duotone" />}
                  label="First Name"
                  value={user?.firstName ? capitalizeWordsInSentence(user?.firstName) : ''}
                />
                <InfoItem
                  icon={<UserCircle size={18} weight="duotone" />}
                  label="Last Name"
                  value={user?.lastName ? capitalizeWordsInSentence(user?.lastName) : ''}
                />
                <InfoItem
                  icon={<Briefcase size={18} weight="duotone" />}
                  label="Job Title"
                  value={user?.title}
                />
                <InfoItem
                  icon={<EnvelopeSimple size={18} weight="duotone" />}
                  label="Email Address"
                  value={user?.email}
                />
                <InfoItem
                  icon={<Phone size={18} weight="duotone" />}
                  label="Phone Number"
                  value={user?.phoneNumber}
                />
              </div>
            </div>

            {/* Company Information Card */}
            {supplier && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2
                  className="text-lg font-semibold mb-4 flex items-center gap-2"
                  style={{ color: COLORS.TEXT_PRIMARY }}>
                  <Buildings size={20} weight="duotone" style={{ color: COLORS.PRIMARY_500 }} />
                  Company Information
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <InfoItem
                    icon={<Buildings size={18} weight="duotone" />}
                    label="Company Name"
                    value={supplier.name}
                  />
                  <InfoItem
                    icon={<EnvelopeSimple size={18} weight="duotone" />}
                    label="Company Email"
                    value={supplier.email}
                  />
                  <InfoItem
                    icon={<Gear size={18} weight="duotone" />}
                    label="Integration Type"
                    value={supplier.integrationType}
                  />

                  <InfoItem
                    icon={<MapPin size={18} weight="duotone" />}
                    label="Address"
                    value={
                      supplier.address
                        ? `${supplier.address}${supplier.city ? `, ${supplier.city}` : ''}${
                            supplier.state ? `, ${supplier.state}` : ''
                          }${supplier.zipCode ? ` ${supplier.zipCode}` : ''}`
                        : null
                    }
                  />

                  {/* <InfoItem */}
                  {/*   icon={<SealCheck size={18} weight="duotone" />} */}
                  {/*   label="Verification Status" */}
                  {/*   value={supplier.isFullyVerified ? 'Fully Verified' : 'Pending Verification'} */}
                  {/* /> */}
                </div>

                {/* Credit Application Emails */}
                {supplier.creditApplicationSetting?.creditApplicationEmails && (
                  <div className="mt-4">
                    <PillsList
                      icon={<EnvelopeSimple size={18} weight="duotone" />}
                      label="Credit Application Emails"
                      items={supplier.creditApplicationSetting.creditApplicationEmails}
                      emptyMessage="No emails configured"
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Quick Stats Card */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-semibold mb-4" style={{ color: COLORS.TEXT_PRIMARY }}>
                Account Status
              </h2>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-semibold mb-4" style={{ color: COLORS.TEXT_PRIMARY }}>
                Quick Actions
              </h2>
              <div className="space-y-2">{/* Quick action buttons can be added here */}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Profile Modal */}
      <EditProfileModal
        open={isEditProfileModalOpen}
        handleClose={(editSuccess) => {
          setIsEditProfileModalOpen(false);
        }}
      />
    </div>
  );
};

export default SupplierUserProfile;
